import {
  fleetAssignmentListSchema,
  fleetAssignmentSchema,
  fleetDamageListSchema,
  fleetDamageReportSchema,
  fleetDepartmentListSchema,
  fleetDocumentListSchema,
  fleetDocumentSchema,
  fleetWorkshopVisitListSchema,
  fleetWorkshopVisitSchema,
  fleetDriverDetailSchema,
  fleetDriverListSchema,
  fleetDriverProfileSchema,
  fleetInspectionListSchema,
  fleetInspectionSchema,
  fleetInstructionSessionSchema,
  fleetLicenseCheckSchema,
  fleetManualTaskResultSchema,
  fleetMeComplianceSchema,
  fleetOdometerListSchema,
  fleetOdometerReadingSchema,
  fleetPersonListSchema,
  fleetPolicySchema,
  fleetServiceDueSchema,
  fleetChargeConnectorListSchema,
  fleetChargePasswordSchema,
  fleetChargeStationCreatedSchema,
  fleetChargeStationListSchema,
  fleetChargeStationSchema,
  fleetChargeTagLinkedSchema,
  fleetChargeVehicleSchema,
  fleetEnergyCommandResponseSchema,
  fleetEnergyGroupCreatedSchema,
  fleetSepaFileSchema,
  fleetTariffChipSchema,
  fleetTariffSettingsSchema,
  fleetEnergyOverviewSchema,
  fleetSolarSchema,
  fleetSummarySchema,
  fleetTaskListSchema,
  fleetVehicleDetailSchema,
  fleetVehicleListSchema,
  fleetVehicleSchema,
  type FleetOperationalStatus,
  type FleetTaskGroupValue,
  type FleetUsageMode,
  type PutFleetChargeTagRequest,
  type RotateFleetChargePasswordRequest,
  type UpdateFleetChargeStationRequest,
  type SaveFleetEnergyGroupRequest,
  type SaveFleetTariffChip,
  type SaveFleetTariffSettings,
  type UpdateFleetTariffChip,
  type UpdateFleetDamageStatusRequest,
  type CloseFleetAssignmentRequest,
  type CreateFleetAssignmentRequest,
  type CreateFleetDamageRequest,
  type CreateFleetInspectionRequest,
  type CreateFleetManualTaskRequest,
  type CreateFleetInstructionRequest,
  type CreateFleetLicenseCheckRequest,
  type CreateFleetOdometerCorrectionRequest,
  type CreateFleetOdometerRequest,
  type CreateFleetChargeStationRequest,
  type CreateFleetPoolBookingRequest,
  type UpdateFleetPoolBookingRequest,
  type CreateFleetWorkshopVisitRequest,
  type FleetVehicleWrite,
  type UpdateFleetDriverProfileRequest,
  type UpdateFleetPolicyRequest,
  type UpdateFleetVehicleRequest,
  okResponseSchema,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_BASE_URL, apiFetch } from '@/lib/api';

export const fleetPolicyKey = ['fleet', 'policy'] as const;
export const fleetVehicleListKey = ['fleet', 'vehicles'] as const;
export const fleetVehicleKey = (id: string) => ['fleet', 'vehicle', id] as const;
export const fleetInspectionKey = (id: string) => ['fleet', 'inspections', id] as const;
export const fleetDepartmentKey = ['fleet', 'departments'] as const;
export const fleetMeComplianceKey = ['fleet', 'me', 'compliance'] as const;
export const fleetDriverListKey = ['fleet', 'drivers'] as const;
export const fleetDriverKey = (userId: string) => ['fleet', 'driver', userId] as const;
export const fleetPeopleKey = (q: string) => ['fleet', 'people', q] as const;
export const fleetPoolVehiclesKey = ['fleet', 'pool-vehicles'] as const;
export const fleetAssignmentsKey = (vehicleId: string) =>
  ['fleet', 'assignments', vehicleId] as const;
export const fleetOdometerKey = (vehicleId: string) => ['fleet', 'odometer', vehicleId] as const;
export const fleetServiceDueKey = (vehicleId: string) =>
  ['fleet', 'service-due', vehicleId] as const;
export const fleetDamagesMineKey = ['fleet', 'damages', 'mine'] as const;
export const fleetDamageKey = (id: string) => ['fleet', 'damage', id] as const;
export const fleetWorkshopKey = (vehicleId: string) => ['fleet', 'workshop', vehicleId] as const;
export const fleetDocumentsKey = (vehicleId: string) => ['fleet', 'documents', vehicleId] as const;
export const fleetDriverDocumentsKey = (userId: string) =>
  ['fleet', 'driver-documents', userId] as const;
export const fleetTasksKey = ['fleet', 'tasks'] as const;
export const fleetSummaryKey = ['fleet', 'summary'] as const;
export const fleetSolarKey = ['fleet', 'solar'] as const;
export const fleetChargeConnectorsKey = ['fleet', 'charge-connectors'] as const;
export const fleetChargeStationsKey = ['fleet', 'charge-stations'] as const;
export const fleetChargeVehicleKey = (id: string) => ['fleet', 'charge-vehicle', id] as const;
export const fleetEnergyKey = ['fleet', 'energy'] as const;
export const fleetDamagesAllKey = ['fleet', 'damages', 'all'] as const;
export const fleetMeAssignmentsKey = ['fleet', 'me', 'assignments'] as const;
export const fleetVehicleDamagesKey = (vehicleId: string) =>
  ['fleet', 'vehicle-damages', vehicleId] as const;

export function useFleetSolar() {
  return useQuery({
    queryKey: fleetSolarKey,
    queryFn: () => apiFetch('/fleet/solar', fleetSolarSchema),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useFleetEnergy() {
  return useQuery({
    queryKey: fleetEnergyKey,
    queryFn: () => apiFetch('/fleet/energy', fleetEnergyOverviewSchema),
    staleTime: 15_000,
    refetchInterval: 15_000,
  });
}

export function useSaveFleetEnergyGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      body: SaveFleetEnergyGroupRequest;
    }): Promise<{ ok: true } | { id: string }> => {
      if (input.id) {
        return apiFetch(`/fleet/energy/groups/${input.id}`, okResponseSchema, {
          method: 'PUT',
          body: JSON.stringify(input.body),
        });
      }
      return apiFetch('/fleet/energy/groups', fleetEnergyGroupCreatedSchema, {
        method: 'POST',
        body: JSON.stringify(input.body),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetEnergyKey });
    },
  });
}

export function useDeleteFleetEnergyGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/fleet/energy/groups/${id}`, okResponseSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetEnergyKey });
    },
  });
}

export function useSetFleetConnectorLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { connectorId: string; limitAmps: number | null }) =>
      apiFetch(`/fleet/energy/connectors/${input.connectorId}/limit`, okResponseSchema, {
        method: 'PUT',
        body: JSON.stringify({ limitAmps: input.limitAmps }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetEnergyKey });
    },
  });
}

export function useSetFleetEnergyDeparture() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; departureAt: string | null }) =>
      apiFetch(`/fleet/energy/sessions/${input.sessionId}/departure`, okResponseSchema, {
        method: 'PUT',
        body: JSON.stringify({ departureAt: input.departureAt }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetEnergyKey });
    },
  });
}

export function useSaveFleetOcmfKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { stationId: string; publicKey: string }) =>
      apiFetch(`/fleet/energy/stations/${input.stationId}/ocmf-key`, okResponseSchema, {
        method: 'PUT',
        body: JSON.stringify({ publicKey: input.publicKey }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetEnergyKey });
    },
  });
}

export function useSaveFleetSessionOcmf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; text: string }) =>
      apiFetch(`/fleet/energy/sessions/${input.sessionId}/ocmf`, okResponseSchema, {
        method: 'POST',
        body: JSON.stringify({ text: input.text }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetEnergyKey });
    },
  });
}

export function useFleetEnergyCommand() {
  return useMutation({
    mutationFn: (input: { path: string; body?: unknown }) =>
      apiFetch(input.path, fleetEnergyCommandResponseSchema, {
        method: 'POST',
        ...(input.body === undefined ? {} : { body: JSON.stringify(input.body) }),
      }),
  });
}

export function useSaveFleetTariff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveFleetTariffSettings) =>
      apiFetch('/fleet/energy/tariff', fleetTariffSettingsSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetEnergyKey });
    },
  });
}

export function useCreateFleetTariffChip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveFleetTariffChip) =>
      apiFetch('/fleet/energy/tariff-chips', fleetTariffChipSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetEnergyKey });
    },
  });
}

export function useUpdateFleetTariffChip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: UpdateFleetTariffChip }) =>
      apiFetch(`/fleet/energy/tariff-chips/${input.id}`, fleetTariffChipSchema, {
        method: 'PUT',
        body: JSON.stringify(input.body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetEnergyKey });
    },
  });
}

export function useReleaseFleetTariffChip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/fleet/energy/tariff-chips/${id}/release`, okResponseSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetEnergyKey });
    },
  });
}

export function usePayFleetInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/fleet/energy/invoices/${id}/paid`, okResponseSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetEnergyKey });
    },
  });
}

export function useFleetSepaFile() {
  return useMutation({
    mutationFn: () => apiFetch('/fleet/energy/invoices/sepa', fleetSepaFileSchema),
  });
}

export function useFleetChargeConnectors(enabled = true) {
  return useQuery({
    queryKey: fleetChargeConnectorsKey,
    queryFn: () => apiFetch('/fleet/charging/connectors', fleetChargeConnectorListSchema),
    enabled,
    staleTime: 15_000,
    refetchInterval: 15_000,
  });
}

export function useFleetChargeStations(enabled = true) {
  return useQuery({
    queryKey: fleetChargeStationsKey,
    queryFn: () => apiFetch('/fleet/charging/stations', fleetChargeStationListSchema),
    enabled,
    staleTime: 15_000,
    refetchInterval: enabled ? 15_000 : false,
  });
}

export function useFleetChargeVehicle(vehicleId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: fleetChargeVehicleKey(vehicleId ?? 'none'),
    queryFn: () => apiFetch(`/fleet/charging/vehicles/${vehicleId}`, fleetChargeVehicleSchema),
    enabled: enabled && Boolean(vehicleId),
    staleTime: 15_000,
    refetchInterval: enabled ? 15_000 : false,
  });
}

export function useCreateFleetChargeStation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFleetChargeStationRequest) =>
      apiFetch('/fleet/charging/stations', fleetChargeStationCreatedSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetChargeStationsKey });
      await queryClient.invalidateQueries({ queryKey: fleetChargeConnectorsKey });
    },
  });
}

export function useUpdateFleetChargeStation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateFleetChargeStationRequest }) =>
      apiFetch(`/fleet/charging/stations/${id}`, fleetChargeStationSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetChargeStationsKey });
      await queryClient.invalidateQueries({ queryKey: fleetChargeConnectorsKey });
    },
  });
}

export function useRotateFleetChargePassword() {
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RotateFleetChargePasswordRequest }) =>
      apiFetch(`/fleet/charging/stations/${id}/password`, fleetChargePasswordSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function usePutFleetChargeTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PutFleetChargeTagRequest) =>
      apiFetch('/fleet/charging/tags', fleetChargeTagLinkedSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetChargeStationsKey });
      await queryClient.invalidateQueries({ queryKey: fleetChargeConnectorsKey });
    },
  });
}

export function useFleetSummary(enabled = true) {
  return useQuery({
    queryKey: fleetSummaryKey,
    queryFn: () => apiFetch('/fleet/summary', fleetSummarySchema),
    enabled,
  });
}

export function useFleetTasks(
  enabled = true,
  options?: { page?: number; group?: FleetTaskGroupValue; status?: 'open' | 'cancelled' },
) {
  const search = new URLSearchParams();
  if (options?.group) {
    search.set('group', options.group);
  }
  if (options?.status) {
    search.set('status', options.status);
  }
  if (options?.page != null) {
    search.set('page', String(options.page));
    search.set('pageSize', '25');
  }
  const query = search.toString();
  return useQuery({
    queryKey: [
      ...fleetTasksKey,
      options?.status ?? 'open',
      options?.group ?? 'all',
      options?.page ?? 'all',
    ],
    queryFn: () => apiFetch(`/fleet/tasks${query ? `?${query}` : ''}`, fleetTaskListSchema),
    enabled,
  });
}

export function useFleetPolicy() {
  return useQuery({
    queryKey: fleetPolicyKey,
    queryFn: () => apiFetch('/fleet/policy', fleetPolicySchema),
  });
}

export function useUpdateFleetPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateFleetPolicyRequest) =>
      apiFetch('/fleet/policy', fleetPolicySchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetPolicyKey });
    },
  });
}

export type FleetVehicleListFilters = {
  mine?: boolean;
  page?: number;
  q?: string;
  operationalStatus?: FleetOperationalStatus;
  usageMode?: FleetUsageMode;
};

export function useFleetVehicles(includeRetired: boolean, options?: FleetVehicleListFilters) {
  const params = new URLSearchParams();
  if (includeRetired) {
    params.set('includeRetired', 'true');
  }
  if (options?.mine) {
    params.set('mine', 'true');
  }
  if (options?.q?.trim()) {
    params.set('q', options.q.trim());
  }
  if (options?.operationalStatus) {
    params.set('operationalStatus', options.operationalStatus);
  }
  if (options?.usageMode) {
    params.set('usageMode', options.usageMode);
  }
  if (options?.page != null) {
    params.set('page', String(options.page));
    params.set('pageSize', '25');
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  return useQuery({
    queryKey: [
      ...fleetVehicleListKey,
      includeRetired,
      options?.mine === true,
      options?.q ?? '',
      options?.operationalStatus ?? '',
      options?.usageMode ?? '',
      options?.page ?? 'all',
    ],
    queryFn: () => apiFetch(`/fleet/vehicles${query}`, fleetVehicleListSchema),
  });
}

export function useFleetVehicle(id: string) {
  return useQuery({
    queryKey: fleetVehicleKey(id),
    queryFn: () => apiFetch(`/fleet/vehicles/${id}`, fleetVehicleDetailSchema),
  });
}

export function useFleetDepartments() {
  return useQuery({
    queryKey: fleetDepartmentKey,
    queryFn: () => apiFetch('/fleet/departments', fleetDepartmentListSchema),
  });
}

export function useFleetInspections(id: string) {
  return useQuery({
    queryKey: fleetInspectionKey(id),
    queryFn: () => apiFetch(`/fleet/vehicles/${id}/inspections`, fleetInspectionListSchema),
  });
}

export function useCreateFleetVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: FleetVehicleWrite) =>
      apiFetch('/fleet/vehicles', fleetVehicleSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetVehicleListKey });
    },
  });
}

export function useUpdateFleetVehicle(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateFleetVehicleRequest) =>
      apiFetch(`/fleet/vehicles/${id}`, fleetVehicleSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetVehicleKey(id) });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleListKey });
    },
  });
}

export function useRetireFleetVehicle(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/fleet/vehicles/${id}/retire`, fleetVehicleSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetVehicleKey(id) });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleListKey });
      await queryClient.invalidateQueries({ queryKey: fleetSummaryKey });
      await queryClient.invalidateQueries({ queryKey: fleetTasksKey });
    },
  });
}

export function useDeleteFleetVehicle(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/fleet/vehicles/${id}`, fleetManualTaskResultSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: fleetVehicleKey(id) });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleListKey });
      await queryClient.invalidateQueries({ queryKey: fleetSummaryKey });
      await queryClient.invalidateQueries({ queryKey: fleetTasksKey });
    },
  });
}

export function useRecordFleetInspection(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFleetInspectionRequest) =>
      apiFetch(`/fleet/vehicles/${id}/inspections`, fleetInspectionSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetVehicleKey(id) });
      await queryClient.invalidateQueries({ queryKey: fleetInspectionKey(id) });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleListKey });
    },
  });
}

export function useFleetMeCompliance() {
  return useQuery({
    queryKey: fleetMeComplianceKey,
    queryFn: () => apiFetch('/fleet/me/compliance', fleetMeComplianceSchema),
  });
}

export function useFleetDrivers(page?: number, q?: string) {
  const search = new URLSearchParams();
  if (page != null) {
    search.set('page', String(page));
    search.set('pageSize', '25');
  }
  const trimmed = q?.trim() ?? '';
  if (trimmed.length >= 2) {
    search.set('q', trimmed);
  }
  const query = search.toString();
  return useQuery({
    queryKey: [...fleetDriverListKey, page ?? 'all', trimmed.length >= 2 ? trimmed : ''],
    queryFn: () => apiFetch(`/fleet/drivers${query ? `?${query}` : ''}`, fleetDriverListSchema),
  });
}

export function useFleetDriver(userId: string) {
  return useQuery({
    queryKey: fleetDriverKey(userId),
    queryFn: () => apiFetch(`/fleet/drivers/${userId}`, fleetDriverDetailSchema),
    enabled: Boolean(userId),
  });
}

export function useFleetPeople(
  q: string,
  options: { minLength?: number; enabled?: boolean; scope?: 'active' } = {},
) {
  const minLength = options.minLength ?? 0;
  const params = new URLSearchParams({ q });
  if (options.scope) {
    params.set('scope', options.scope);
  }
  return useQuery({
    queryKey: [...fleetPeopleKey(q), options.scope ?? 'license'],
    queryFn: () => apiFetch(`/fleet/people?${params.toString()}`, fleetPersonListSchema),
    enabled: (options.enabled ?? true) && q.trim().length >= minLength,
  });
}

export function useCreateFleetManualTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFleetManualTaskRequest) =>
      apiFetch('/fleet/tasks', fleetManualTaskResultSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetTasksKey });
    },
  });
}

export function useCompleteFleetManualTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/fleet/tasks/${id}/complete`, fleetManualTaskResultSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetTasksKey });
      await queryClient.invalidateQueries({ queryKey: fleetSummaryKey });
    },
  });
}

export function useCancelFleetManualTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/fleet/tasks/${id}/cancel`, fleetManualTaskResultSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetTasksKey });
      await queryClient.invalidateQueries({ queryKey: fleetSummaryKey });
    },
  });
}

export function useUpsertFleetDriverProfile(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateFleetDriverProfileRequest) =>
      apiFetch(`/fleet/drivers/${userId}/profile`, fleetDriverProfileSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetDriverKey(userId) });
      await queryClient.invalidateQueries({ queryKey: fleetDriverListKey });
    },
  });
}

export function useRecordFleetLicenseCheck(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFleetLicenseCheckRequest & { file?: File }) => {
      const { file, ...check } = body;
      if (file) {
        const form = new FormData();
        form.append('payload', JSON.stringify(check));
        form.append('file', file);
        return apiFetch(`/fleet/drivers/${userId}/license-checks`, fleetLicenseCheckSchema, {
          method: 'POST',
          body: form,
        });
      }
      return apiFetch(`/fleet/drivers/${userId}/license-checks`, fleetLicenseCheckSchema, {
        method: 'POST',
        body: JSON.stringify(check),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetDriverKey(userId) });
      await queryClient.invalidateQueries({ queryKey: fleetDriverListKey });
      await queryClient.invalidateQueries({ queryKey: fleetDriverDocumentsKey(userId) });
      await queryClient.invalidateQueries({ queryKey: fleetMeComplianceKey });
    },
  });
}

export function useCreateFleetInstruction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFleetInstructionRequest) =>
      apiFetch('/fleet/instructions', fleetInstructionSessionSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetDriverListKey });
      await queryClient.invalidateQueries({ queryKey: ['fleet', 'driver'] });
      await queryClient.invalidateQueries({ queryKey: fleetMeComplianceKey });
    },
  });
}

export function useFleetPoolVehicles() {
  return useQuery({
    queryKey: fleetPoolVehiclesKey,
    queryFn: () => apiFetch('/fleet/pool-vehicles', fleetVehicleListSchema),
  });
}

export function useFleetAssignments(vehicleId: string) {
  return useQuery({
    queryKey: fleetAssignmentsKey(vehicleId),
    queryFn: () => apiFetch(`/fleet/vehicles/${vehicleId}/assignments`, fleetAssignmentListSchema),
    enabled: Boolean(vehicleId),
  });
}

export function useCreateFleetAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFleetAssignmentRequest) =>
      apiFetch('/fleet/assignments', fleetAssignmentSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: fleetAssignmentsKey(result.vehicleId) });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleListKey });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleKey(result.vehicleId) });
    },
  });
}

export function useCloseFleetAssignment(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CloseFleetAssignmentRequest }) =>
      apiFetch(`/fleet/assignments/${id}/close`, fleetAssignmentSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetAssignmentsKey(vehicleId) });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleListKey });
    },
  });
}

export function useCreateFleetPoolBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFleetPoolBookingRequest) =>
      apiFetch('/fleet/pool-bookings', fleetAssignmentSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetPoolVehiclesKey });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleListKey });
      await queryClient.invalidateQueries({ queryKey: ['fleet', 'assignments'] });
      await queryClient.invalidateQueries({ queryKey: fleetMeAssignmentsKey });
    },
  });
}

function usePoolBookingChange() {
  const queryClient = useQueryClient();
  return async (vehicleId: string) => {
    await queryClient.invalidateQueries({ queryKey: fleetPoolVehiclesKey });
    await queryClient.invalidateQueries({ queryKey: fleetVehicleListKey });
    await queryClient.invalidateQueries({ queryKey: fleetAssignmentsKey(vehicleId) });
    await queryClient.invalidateQueries({ queryKey: ['fleet', 'assignments'] });
    await queryClient.invalidateQueries({ queryKey: fleetMeAssignmentsKey });
  };
}

export function useUpdateFleetPoolBooking() {
  const refresh = usePoolBookingChange();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateFleetPoolBookingRequest }) =>
      apiFetch(`/fleet/pool-bookings/${id}`, fleetAssignmentSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async (result) => {
      await refresh(result.vehicleId);
    },
  });
}

export function useEndFleetPoolBookingEarly() {
  const refresh = usePoolBookingChange();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/fleet/pool-bookings/${id}/end-early`, fleetAssignmentSchema, {
        method: 'POST',
      }),
    onSuccess: async (result) => {
      await refresh(result.vehicleId);
    },
  });
}

export function useDeleteFleetPoolBooking() {
  const refresh = usePoolBookingChange();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/fleet/pool-bookings/${id}`, fleetAssignmentSchema, {
        method: 'DELETE',
      }),
    onSuccess: async (result) => {
      await refresh(result.vehicleId);
    },
  });
}

export function useFleetOdometer(vehicleId: string) {
  return useQuery({
    queryKey: fleetOdometerKey(vehicleId),
    queryFn: () => apiFetch(`/fleet/vehicles/${vehicleId}/odometer`, fleetOdometerListSchema),
    enabled: Boolean(vehicleId),
  });
}

export function useFleetServiceDue(vehicleId: string) {
  return useQuery({
    queryKey: fleetServiceDueKey(vehicleId),
    queryFn: () => apiFetch(`/fleet/vehicles/${vehicleId}/service-due`, fleetServiceDueSchema),
    enabled: Boolean(vehicleId),
  });
}

export function useRecordFleetOdometer(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFleetOdometerRequest) =>
      apiFetch(`/fleet/vehicles/${vehicleId}/odometer`, fleetOdometerReadingSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetOdometerKey(vehicleId) });
      await queryClient.invalidateQueries({ queryKey: fleetServiceDueKey(vehicleId) });
    },
  });
}

export function useCorrectFleetOdometer(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFleetOdometerCorrectionRequest) =>
      apiFetch(`/fleet/vehicles/${vehicleId}/odometer/corrections`, fleetOdometerReadingSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetOdometerKey(vehicleId) });
      await queryClient.invalidateQueries({ queryKey: fleetServiceDueKey(vehicleId) });
    },
  });
}

export function useFleetDamagesMine() {
  return useQuery({
    queryKey: fleetDamagesMineKey,
    queryFn: () => apiFetch('/fleet/damages?scope=mine', fleetDamageListSchema),
  });
}

export function useFleetDamagesAll(enabled = true) {
  return useQuery({
    queryKey: fleetDamagesAllKey,
    queryFn: () => apiFetch('/fleet/damages?scope=all', fleetDamageListSchema),
    enabled,
  });
}

export function useFleetVehicleDamages(vehicleId: string) {
  return useQuery({
    queryKey: fleetVehicleDamagesKey(vehicleId),
    queryFn: () => apiFetch(`/fleet/vehicles/${vehicleId}/damages`, fleetDamageListSchema),
    enabled: Boolean(vehicleId),
  });
}

export function useUpdateFleetDamageStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateFleetDamageStatusRequest }) =>
      apiFetch(`/fleet/damages/${id}/status`, fleetDamageReportSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: fleetDamageKey(result.id) });
      await queryClient.invalidateQueries({ queryKey: fleetDamagesMineKey });
      await queryClient.invalidateQueries({ queryKey: fleetDamagesAllKey });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleDamagesKey(result.vehicleId) });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleKey(result.vehicleId) });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleListKey });
      await queryClient.invalidateQueries({ queryKey: fleetTasksKey });
      await queryClient.invalidateQueries({ queryKey: fleetSummaryKey });
    },
  });
}

export function useReleaseFleetVehicle(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/fleet/vehicles/${vehicleId}/release`, okResponseSchema, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetVehicleKey(vehicleId) });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleListKey });
      await queryClient.invalidateQueries({ queryKey: fleetTasksKey });
      await queryClient.invalidateQueries({ queryKey: fleetSummaryKey });
    },
  });
}

export function useFleetMeAssignments(from?: string, to?: string) {
  const params = new URLSearchParams();
  if (from) {
    params.set('from', from);
  }
  if (to) {
    params.set('to', to);
  }
  const query = params.toString();
  return useQuery({
    queryKey: [...fleetMeAssignmentsKey, from ?? '', to ?? ''],
    queryFn: () =>
      apiFetch(`/fleet/me/assignments${query ? `?${query}` : ''}`, fleetAssignmentListSchema),
  });
}

export function useFleetDamage(id: string) {
  return useQuery({
    queryKey: fleetDamageKey(id),
    queryFn: () => apiFetch(`/fleet/damages/${id}`, fleetDamageReportSchema),
    enabled: Boolean(id),
  });
}

export function useFleetWorkshopVisits(vehicleId: string) {
  return useQuery({
    queryKey: fleetWorkshopKey(vehicleId),
    queryFn: () =>
      apiFetch(`/fleet/vehicles/${vehicleId}/workshop-visits`, fleetWorkshopVisitListSchema),
    enabled: Boolean(vehicleId),
  });
}

export function useCreateFleetWorkshopVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFleetWorkshopVisitRequest) =>
      apiFetch('/fleet/workshop-visits', fleetWorkshopVisitSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: fleetWorkshopKey(result.vehicleId) });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleKey(result.vehicleId) });
      await queryClient.invalidateQueries({ queryKey: fleetOdometerKey(result.vehicleId) });
      await queryClient.invalidateQueries({ queryKey: fleetServiceDueKey(result.vehicleId) });
    },
  });
}

export function useFleetVehicleDocuments(vehicleId: string) {
  return useQuery({
    queryKey: fleetDocumentsKey(vehicleId),
    queryFn: () => apiFetch(`/fleet/vehicles/${vehicleId}/documents`, fleetDocumentListSchema),
    enabled: Boolean(vehicleId),
  });
}

export function useFleetDriverDocuments(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: fleetDriverDocumentsKey(userId),
    queryFn: () => apiFetch(`/fleet/drivers/${userId}/documents`, fleetDocumentListSchema),
    enabled: Boolean(userId) && enabled,
  });
}

export function useUploadFleetDocument(vehicleId?: string, userId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: string; file: File; vehicleId?: string; userId?: string }) => {
      const form = new FormData();
      form.append('kind', input.kind);
      form.append('file', input.file);
      const targetVehicleId = input.vehicleId ?? vehicleId;
      const targetUserId = input.userId ?? userId;
      if (targetVehicleId) {
        form.append('vehicleId', targetVehicleId);
      }
      if (targetUserId) {
        form.append('userId', targetUserId);
      }
      return apiFetch('/fleet/documents', fleetDocumentSchema, {
        method: 'POST',
        body: form,
      });
    },
    onSuccess: async (result) => {
      if (result.vehicleId) {
        await queryClient.invalidateQueries({ queryKey: fleetDocumentsKey(result.vehicleId) });
      }
      if (result.userId) {
        await queryClient.invalidateQueries({ queryKey: fleetDriverDocumentsKey(result.userId) });
      }
    },
  });
}

export function useDeleteFleetDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/fleet/documents/${id}`, fleetManualTaskResultSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['fleet', 'documents'] });
      await queryClient.invalidateQueries({ queryKey: ['fleet', 'driver-documents'] });
      await queryClient.invalidateQueries({ queryKey: ['fleet', 'vehicle-damages'] });
      await queryClient.invalidateQueries({ queryKey: ['fleet', 'damage'] });
    },
  });
}

export function fleetDocumentDownloadHref(documentId: string): string {
  return `${API_BASE_URL}/fleet/documents/${documentId}`;
}

export function useCreateFleetDamage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFleetDamageRequest & { photos?: File[] }) => {
      const form = new FormData();
      form.append('vehicleId', input.vehicleId);
      form.append('kind', input.kind);
      form.append('severity', input.severity);
      form.append('description', input.description);
      if (input.locationNote) {
        form.append('locationNote', input.locationNote);
      }
      for (const photo of input.photos ?? []) {
        form.append('photos', photo);
      }
      return apiFetch('/fleet/damages', fleetDamageReportSchema, {
        method: 'POST',
        body: form,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetDamagesMineKey });
      await queryClient.invalidateQueries({ queryKey: fleetDamagesAllKey });
      await queryClient.invalidateQueries({ queryKey: fleetVehicleListKey });
      await queryClient.invalidateQueries({ queryKey: fleetTasksKey });
      await queryClient.invalidateQueries({ queryKey: fleetSummaryKey });
    },
  });
}
